import { type WrappedInsightPlanningAheadAttributes } from "@clipboard-health/contract-backend-main";
import { Image, Span, Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";

import clockIcon from "../../assets/clock-wrapped.png";

const HOURS_PER_DAY = 24;
const DAYS_FOR_WEEKS_THRESHOLD = 14;
const HOURS_FOR_WEEKS_THRESHOLD = HOURS_PER_DAY * DAYS_FOR_WEEKS_THRESHOLD;

function formatBookingLeadTime(hours: number): { value: string; suffix: string } {
  if (hours >= HOURS_FOR_WEEKS_THRESHOLD) {
    const weeks = hours / (HOURS_PER_DAY * 7);
    return { value: weeks.toFixed(0), suffix: "weeks" };
  }

  if (hours >= HOURS_PER_DAY) {
    const days = hours / HOURS_PER_DAY;
    return { value: days.toFixed(0), suffix: "days" };
  }

  return { value: Math.round(hours).toString(), suffix: "hours" };
}

interface PlanningAheadContentProps {
  attributes: WrappedInsightPlanningAheadAttributes;
}

export function PlanningAheadContent(props: PlanningAheadContentProps) {
  const { attributes } = props;
  const { value: leadTimeValue, suffix: leadTimeSuffix } = formatBookingLeadTime(
    attributes.averageBookingLeadHours
  );

  return (
    <Stack flex={1} position="relative" overflow="hidden">
      {/* Text section - top left */}
      <Stack
        position="absolute"
        sx={{
          top: "12%",
          left: "7.7%",
        }}
        gap={1}
      >
        <Text variant="h3" color="white">
          Way to plan ahead!
        </Text>
        <Text variant="body1" color="white" sx={{ marginTop: 2 }}>
          On average, you picked up shifts
        </Text>
        <Text variant="h1" color="white" sx={{ fontSize: "5rem", lineHeight: 1 }}>
          {leadTimeValue}
          <Span sx={{ fontSize: "1rem", marginLeft: 1 }}>
            {leadTimeSuffix} before they started.
          </Span>
        </Text>
      </Stack>

      {/* Clock - positioned based on Figma: 319x337 at top 354px, left 47.55px, angle -3.85deg on 375x812 page */}
      <Box
        position="absolute"
        sx={{
          top: "43.6%", // 354 / 812
          left: "12.7%", // 47.55 / 375
        }}
      >
        <Image
          src={clockIcon}
          alt="Clock"
          width="85vw" // 319 / 375
          height="auto"
          sx={{ transform: "rotate(-3.85deg)" }}
        />
      </Box>
    </Stack>
  );
}
